import { View } from '../../primitives/View';
import { brandColor } from '../../styles/brand-colors';
import { useTheme } from '../../theme';
import { withAlpha } from '../../utils/color';
import { Badge } from '../Badge';
import { CardChipTooltip } from './CardChipTooltip';

/**
 * The "To-do" chip itself — how many flashcards are waiting to be studied.
 * Visually it IS the DS Badge's glass/primary `status` variant at size `s`:
 * 28px tall, `rounded-8`, 8px `-600` dot, gap 4, L3 12/16 Mona Bold `-600`
 * text over the `-50` fill + `-100` border. The one override is the RIGHT
 * padding: both nodes read a flat `px-2`, where the `status` type's own S ramp
 * trails at `pr-4`.
 *
 * Two homes, one definition: the grid cards' top-right corner overlay
 * ({@link CardTodoBadge}, Figma 5822:49182) and the LEADING chip of a list
 * row's badge row (`CardChips`, Figma 5868:74056).
 */
export function TodoChip({
  count,
  label,
  tooltip,
  pinned = false,
}: {
  count: number;
  label?: string;
  /** Hover copy spelling the count out. Omit for no tooltip. */
  tooltip?: string;
  /**
   * Hold the chip at the 28px the design states, instead of letting the tablet
   * rem ramp grow it — set by the compact grid tile, whose 180×180 box is
   * fixed px at every scale (unpinned it reached 35px there, half again the
   * height of the 24px chips in the panel below).
   */
  pinned?: boolean;
}) {
  const { isDark } = useTheme();
  // Frost, like the globe chip beside it: both sit over the cover, and glass
  // is how a chip reads as cut out of the art rather than laid on top of it.
  //
  // The fill has to soften for the blur to show at all — Figma's `130:4751`
  // states an OPAQUE `primary-50`, which would hide the frost completely. 70%
  // is the alpha the sibling chip's own node states for its white
  // (`130:4750` = `brand/secondary/30`), so both cover chips dissolve at the
  // same rate instead of each picking a number.
  //
  // An inline style, not `bg-primary-50/70`: an alpha utility is
  // runtime-processed by NativeWind, and toggling one in place is the bogus
  // "Couldn't find a navigation context" Render Error the
  // `no-runtime-class-toggle` rule exists for (and why the card surfaces use
  // `CARD_SURFACE_ALPHA`). Reading the palette through `brandColor` keeps dark
  // mode inverting, which the class it replaces did for free.
  //
  // DARK holds the fill at 90%, not 70%: dark `primary-50` is a deep violet,
  // and at 70% over a white PDF page or a bright cover it washed out to a
  // mid-lavender that the `-600` label (also a mid-lavender in dark) sank
  // into — 2.2:1 on a white page. At 90% the chip stays a dark violet over
  // any art, and the label steps up to `-700` (below), ≥ 5.6:1 on every
  // backdrop measured. Light is unchanged (its pale fill over pale art never
  // had the problem).
  //
  // Web reads the palette VARIABLE, so the chip follows whatever the dark
  // ramp is re-pointed to; native has no CSS vars and takes the hex.
  const fillAlpha = isDark ? 0.9 : 0.7;
  const webFill = { backgroundColor: `rgba(var(--primary-50-rgb), ${fillAlpha})` };
  const nativeFill = {
    backgroundColor: withAlpha(brandColor('primary', '50', isDark), fillAlpha),
  };
  const chip = (
    <Badge
      colour="primary"
      type="status"
      size="s"
      style="glass"
      blur
      pinned={pinned}
      webProps={{ style: webFill }}
      nativeProps={{ style: nativeFill }}
      // Dark only: the `-600` label is the same lavender as the art-tinted
      // fill; `-700` (the DS's own dark active-label step, cf. the
      // SegmentedControl secondary pill) clears it on every cover.
      textClassName="dark:text-primary-700"
      // The one deviation from the `status` ramp: both Figma nodes read a
      // flat `px-2`, where the type's own S ramp trails at `pr-4`. Stated in
      // px for native as well, so a pinned chip's trailing pad holds the 8
      // instead of ramping to 10 and widening the pill.
      className="pr-2 native:pr-[8px]"
      accessibilityLabel={tooltip ?? label}>
      {label ?? count}
    </Badge>
  );
  // Through `CardChipTooltip`, NOT `Tooltip` directly: Radix's `Trigger
  // asChild` clones its child with the hover handlers and a ref, and Badge is
  // a plain function component that drops both — a bare `<Tooltip>` here
  // renders a trigger that never opens. The wrapper's `<span>` takes them.
  // (It also no-ops on native, where hover isn't a thing.) The span sits
  // INSIDE the card's pressable, so taking hover costs nothing — the click
  // still bubbles through to the card underneath.
  return <CardChipTooltip label={tooltip}>{chip}</CardChipTooltip>;
}
(TodoChip as { displayName?: string }).displayName = 'TodoChip';

/**
 * The grid cards' corner "To-do" overlay (Figma 5822:49182). Rendering
 * nothing at `0` / `undefined` lives HERE so DeckCard, FolderCard and
 * StudyGroupCard share the one zero rule.
 */
export function CardTodoBadge({
  count,
  label,
  tooltip,
  top,
  right,
  pinned = false,
}: {
  /** Flashcards to do. `0` / `undefined` → no badge at all. */
  count?: number;
  /** Localized text — `t('deck.todo', { count })` → "To-do: 18". */
  label?: string;
  /** Hover copy spelling the count out ("10 Cards and 8 Questions to-do today"). */
  tooltip?: string;
  /** Offsets from the positioning parent's top / right edges, in px. */
  top: number;
  right: number;
  /** Fixed-px tile: hold the chip's 28px design height (see `TodoChip`). */
  pinned?: boolean;
}) {
  if (!count) return null;
  const position = { top, right };
  return (
    // Non-interactive, so `pointerEvents: 'none'` on native — the chip must
    // never eat a press meant for the card underneath.
    <View
      className="absolute z-10"
      webProps={{ style: position }}
      nativeProps={{ style: position, pointerEvents: 'none' }}>
      <TodoChip count={count} label={label} tooltip={tooltip} pinned={pinned} />
    </View>
  );
}
(CardTodoBadge as { displayName?: string }).displayName = 'CardTodoBadge';
